import apiClient from './apiClient';
import { trackPromise } from 'react-promise-tracker';
import { enqueue, getQueue, isDuplicateTimestampOffline, offlineGet } from './helpers/offlineHelpers';
import { formatLocalDateTime } from './operatorService';

function formatDateISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatDatetimeISO(date) {
    const pad = (n) => n.toString().padStart(2, '0');

    return (
        date.getFullYear() +
        '-' +
        pad(date.getMonth() + 1) +
        '-' +
        pad(date.getDate()) +
        'T' +
        pad(date.getHours()) +
        ':' +
        pad(date.getMinutes()) +
        ':' +
        pad(date.getSeconds())
    );
}

export function getHourTimestamp() {
    const now = new Date();

    return formatLocalDateTime(new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() - 1, 0, 0));
}

async function registerHourlyRecord(payload) {
    // const { data } = await trackPromise(apiClient.post('/api/production/hourly', payload));
    // return data?.data ?? null;
    const enrichedPayload = {
        ...payload,
        timestampHour: payload.timestampHour || getHourTimestamp()
    };

    // 📴 OFFLINE
    if (!navigator.onLine) {
        const currentRecords = await listMyRecords(formatDateISO(new Date(payload.timestampHour || new Date())));

        // 🚫 Validar duplicados SOLO offline
        if (isDuplicateTimestampOffline(enrichedPayload.timestampHour, currentRecords)) {
            throw new Error('Ya existe un registro pendiente con la misma hora');
        }

        enqueue({
            type: 'CREATE_HOURLY_RECORD',
            payload: enrichedPayload
        });

        return {
            ...enrichedPayload,
            offline: true
        };
    }

    // 🌐 ONLINE
    const { data } = await trackPromise(apiClient.post('/api/production/hourly', enrichedPayload));

    return data?.data ?? null;
}

async function getHourlyRecordById(id) {
    const { data } = await trackPromise(apiClient.get(`/api/production/hourly/${id}`));
    return data?.data ?? null;
}

function isSameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

async function listMyRecords(date = formatDateISO(new Date())) {
    // const { data } = await trackPromise(
    //     apiClient.get('/api/production/hourly/my-records', {
    //         params: { date }
    //     })
    // );
    // return data?.data ?? [];
    const records = await offlineGet(
        `my-records-${date}`,
        () =>
            trackPromise(
                apiClient.get('/api/production/hourly/my-records', {
                    params: { date }
                })
            ),
        (response) => response?.data?.data ?? []
    );

    const targetDate = new Date(date);

    const offlineItems = getQueue()
        .filter((item) => {
            if (item.type !== 'CREATE_HOURLY_RECORD') return false;

            const itemDate = new Date(item.payload.timestampHour);

            return isSameDay(itemDate, targetDate);
        })
        .map((item) => ({
            ...item.payload,
            offline: true
        }));

    return [...offlineItems, ...records];
}

async function getMachinList4Production(shift, factory, date = formatDatetimeISO(new Date())) {
    const { data } = await trackPromise(
        apiClient.get('/api/production/machines/assigment/list', {
            params: { date, shift, factory }
        })
    );
    return data.data ?? [];
}

async function getOperators() {
    const { data } = await trackPromise(apiClient.get(`/api/production/operators`));
    return data.data ?? [];
}

async function assignMachine(info) {
    const { data } = await trackPromise(apiClient.post(`/api/production/machines/assigment`, info));
    return data ?? [];
}

async function registerEditRequest(payload) {
    const { data } = await trackPromise(apiClient.post('/api/productionEditRequest/hourly/edit-request', payload));
    return data?.data ?? null;
}

async function getProductionVsTarget({ factoryId = null, shiftId, startDate, machineId = null }) {
    const { data } = await trackPromise(apiClient.post('/api/stats/production-vs-target', { factoryId, shiftId, startDate, machineId }));
    // backend returns the array directly (not wrapped in ApiResponseData)
    return data;
}

async function getYieldByHour({ factoryId = null, shiftId, startDate, machineId = null }) {
    const { data } = await trackPromise(apiClient.post('/api/stats/yield-by-hour', { factoryId, shiftId, startDate, machineId }));
    // backend returns a single YieldByHourDTO object directly
    return data;
}

async function getDowntimesByHour({ factoryId = null, shiftId, startDate, machineId = null }) {
    const { data } = await trackPromise(apiClient.post('/api/stats/downtimes', { factoryId, shiftId, startDate, machineId }));
    // backend returns a single YieldByHourDTO object directly
    return data;
}

async function getTimeCicleByHour({ factoryId = null, shiftId, startDate, machineId = null }) {
    const { data } = await trackPromise(apiClient.post('/api/stats/time-cycles-averages', { factoryId, shiftId, startDate, machineId }));
    // backend returns a single YieldByHourDTO object directly
    return data;
}

async function getDefects({ factoryId = null, shiftId, startDate, machineId = null }) {
    const { data } = await trackPromise(apiClient.post('/api/stats/defects', { factoryId, shiftId, startDate, machineId }));
    // backend returns a single YieldByHourDTO object directly
    return data;
}

async function getLeadersByShift(id, factoryId = null) {
    const { data } = await trackPromise(apiClient.get(`/api/production/leaders/shift/${id}/factory/${factoryId}`));
    return data.data ?? [];
}

async function getOperatorsListForLeaders() {
    const { data } = await trackPromise(apiClient.get(`/api/production/leaders/operators`));
    return data.data ?? [];
}

async function assignLeader(info) {
    const { data } = await trackPromise(apiClient.post(`/api/production/leaders/assigment`, info));
    return data ?? [];
}

async function unassignLeader(info) {
    const { data } = await trackPromise(apiClient.post(`/api/production/leaders/unassigment`, info));
    return data ?? [];
}

const productionService = {
    formatDateISO,
    registerHourlyRecord,
    getHourlyRecordById,
    listMyRecords,
    getMachinList4Production,
    assignMachine,
    registerEditRequest,
    getProductionVsTarget,
    getYieldByHour,
    getDowntimesByHour,
    getTimeCicleByHour,
    getDefects,
    getOperators,
    getLeadersByShift,
    getOperatorsListForLeaders,
    assignLeader,
    unassignLeader
};

export default productionService;
